import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { TagChip } from './ChatInput.types';

export interface TagSuggestionListProps {
  suggestions: TagChip[];
  isLoading: boolean;
  selectedIndex: number;
  onSelect: (tag: TagChip) => void;
  cursorRect: { top: number; left: number } | null;
}

export interface TagSuggestionListHandle {
  scrollToIndex: (index: number) => void;
}

export const TagSuggestionList = forwardRef<TagSuggestionListHandle, TagSuggestionListProps>(
  ({ suggestions, isLoading, selectedIndex, onSelect, cursorRect }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollToIndex: (index: number) => {
        if (containerRef.current) {
          const selectedElement = containerRef.current.querySelector(`[data-index="${index}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ block: 'nearest' });
          }
        }
      },
    }));

    useEffect(() => {
      if (containerRef.current && selectedIndex >= 0) {
        const selectedElement = containerRef.current.querySelector(`[data-index="${selectedIndex}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    }, [selectedIndex]);

    const positionStyle: React.CSSProperties = cursorRect
      ? {
          position: 'fixed',
          top: cursorRect.top,
          left: cursorRect.left,
          transform: 'translateY(calc(-100% - 8px))',
          zIndex: 50,
        }
      : {
          position: 'absolute',
          bottom: '100%',
          left: 0,
          marginBottom: '4px',
          zIndex: 50,
        };

    const baseClassName = 'w-auto min-w-[300px] max-w-[500px] bg-bg-primary border border-border-secondary rounded-bg-v2-md shadow-lg';

    if (isLoading) {
      return (
        <div ref={containerRef} style={positionStyle} className={baseClassName}>
          <div className="px-bg-v2-md py-bg-v2-sm text-bg-v2-xs text-text-tertiary">
            Loading...
          </div>
        </div>
      );
    }

    if (suggestions.length === 0) {
      return (
        <div ref={containerRef} style={positionStyle} className={baseClassName}>
          <div className="px-bg-v2-md py-bg-v2-sm text-bg-v2-xs text-text-tertiary">
            No results found
          </div>
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        style={positionStyle}
        className={`${baseClassName} max-h-[240px] overflow-y-auto modern-scrollbar-spaced`}
      >
        {suggestions.map((tag, index) => (
          <button
            key={tag.id}
            data-index={index}
            onClick={() => onSelect(tag)}
            className={`w-full px-bg-v2-md py-bg-v2-xs flex items-start gap-bg-v2-xs hover:bg-bg-secondary transition-colors text-left ${
              index === selectedIndex ? 'bg-bg-brand-primary-alt' : ''
            }`}
          >
            <div className="flex items-center gap-bg-v2-xs min-w-0 flex-1">
              {tag.sectionName && (
                <>
                  <span className="text-bg-v2-xs font-bg-v2-regular text-text-tertiary flex-shrink-0">
                    {tag.sectionName}
                  </span>
                  <span className="text-text-tertiary flex-shrink-0">›</span>
                </>
              )}
              <span className="text-bg-v2-xs font-bg-v2-medium text-text-primary font-mono break-words">
                {tag.label}
              </span>
            </div>
            {tag.data?.status ? (
              <span className="ml-bg-v2-xs flex-shrink-0 text-bg-v2-xs font-bg-v2-regular text-text-secondary whitespace-nowrap">
                ({tag.data.status})
              </span>
            ) : tag.data?.wave ? (
              <span className="ml-bg-v2-xs flex-shrink-0 text-bg-v2-xs font-bg-v2-regular text-text-secondary whitespace-nowrap">
                (Wave {tag.data.wave})
              </span>
            ) : null}
          </button>
        ))}
      </div>
    );
  }
);

TagSuggestionList.displayName = 'TagSuggestionList';
